import React, { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Card, Table, Button, Tag, Space, Popconfirm, notification,
  Select, Tooltip, Modal, Input, Spin, Typography, Badge,
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, ReloadOutlined,
  EyeOutlined, SearchOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { baseUrl } from '../../config';

const { Text } = Typography;

const authH = () => ({ Authorization: `Bearer ${localStorage.getItem('accessToken')}` });

const TYPE_META = {
  announcement:     { label: 'Announcement',         color: 'blue'    },
  payment_reminder: { label: 'Payment Reminder',     color: 'orange'  },
  monthly_reminder: { label: 'Monthly Samarpan',     color: 'gold'    },
  birthday_system:  { label: 'Birthday (ABRSVS)',    color: 'magenta' },
  birthday_member:  { label: 'Birthday (Member)',    color: 'pink'    },
  thanks:           { label: 'Thanks',               color: 'green'   },
  invite:           { label: 'Village Invite',       color: 'cyan'    },
};

const SCOPE_META = {
  all:          'All Members',
  active:       'Active Members',
  village:      'By Village',
  tehsil:       'By Tehsil',
  district:     'By District',
  specific:     'Specific Members',
  payment_due:  'Payment Due',
  birthday:     'Birthday Members',
  anniversary:  'Anniversary Members',
};

function fmtDateTime(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

// ── Seen-By Modal ─────────────────────────────────────────────────────────────
function SeenByModal({ notifId, onClose }) {
  const [loading,  setLoading]  = useState(false);
  const [rows,     setRows]     = useState([]);
  const [total,    setTotal]    = useState(0);
  const [notif,    setNotif]    = useState(null);
  const [page,     setPage]     = useState(1);
  const [search,   setSearch]   = useState('');
  const [query,    setQuery]    = useState('');

  const load = useCallback(async (pg = 1, q = query) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: pg, limit: 50 });
      if (q) params.set('filter', q);
      const res = await axios.get(
        `${baseUrl}/admin/notifications/${notifId}/readers?${params}`,
        { headers: authH() }
      );
      if (res.data.status) {
        setRows(res.data.result);
        setTotal(res.data.total);
        setNotif(res.data.notification);
        setPage(pg);
      }
    } catch { notification.error({ message: 'Could not load reader list' }); }
    finally { setLoading(false); }
  }, [notifId, query]);

  useEffect(() => { load(1, ''); }, [notifId]); // eslint-disable-line

  const doSearch = () => { setQuery(search); load(1, search); };

  const cols = [
    {
      title: '#', width: 46,
      render: (_, __, i) => <Text type="secondary" style={{ fontSize: 11 }}>{(page - 1) * 50 + i + 1}</Text>,
    },
    {
      title: 'Member', dataIndex: 'name',
      render: (name, r) => (
        <div>
          <div style={{ fontWeight: 600 }}>{name || '—'}</div>
          <div style={{ fontSize: 11, color: '#888' }}>{r.phone_no}</div>
        </div>
      ),
    },
    {
      title: 'Village / District', dataIndex: 'village',
      render: (v, r) => (
        <span style={{ fontSize: 12 }}>
          {[v, r.district].filter(Boolean).join(', ') || '—'}
        </span>
      ),
    },
    {
      title: 'Seen At', dataIndex: 'read_at', width: 160,
      render: d => (
        <span style={{ fontSize: 12, color: '#15803d', whiteSpace: 'nowrap' }}>
          {fmtDateTime(d)}
        </span>
      ),
    },
  ];

  return (
    <Modal
      open={true}
      onCancel={onClose}
      footer={null}
      width={780}
      title={
        <Space>
          <EyeOutlined style={{ color: '#1d4ed8' }} />
          <span>Seen By</span>
          {notif && (
            <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
              — {notif.title}
            </Text>
          )}
          <Badge
            count={total}
            style={{ backgroundColor: '#15803d', fontSize: 11 }}
            overflowCount={9999}
          />
        </Space>
      }
    >
      {/* Search bar */}
      <Space style={{ marginBottom: 12, width: '100%' }}>
        <Input
          placeholder="Search by name or phone…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          onPressEnter={doSearch}
          style={{ width: 240 }}
          allowClear
          onClear={() => { setSearch(''); setQuery(''); load(1, ''); }}
        />
        <Button icon={<SearchOutlined />} onClick={doSearch}>Search</Button>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {total} / {notif?.total_sent || '?'} seen
        </Text>
      </Space>

      <Spin spinning={loading}>
        <Table
          dataSource={rows}
          columns={cols}
          rowKey="user_id"
          size="small"
          pagination={{
            current: page,
            pageSize: 50,
            total,
            onChange: pg => load(pg),
            showTotal: t => `${t} members`,
            size: 'small',
          }}
          locale={{ emptyText: 'No one has seen this notification yet.' }}
        />
      </Spin>
    </Modal>
  );
}

// ── Main List ─────────────────────────────────────────────────────────────────
export default function AdminNotificationsList() {
  const history = useHistory();
  const [data,         setData]         = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [typeFilter,   setTypeFilter]   = useState('');
  const [pagination,   setPagination]   = useState({ current: 1, pageSize: 20, total: 0 });
  const [seenNotifId,  setSeenNotifId]  = useState(null);

  const loadList = useCallback(async (pg = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: pg, limit: pagination.pageSize });
      if (typeFilter) params.set('type', typeFilter);
      const res = await axios.get(`${baseUrl}/admin/notifications?${params}`, { headers: authH() });
      if (res.data.status) {
        setData(res.data.result);
        setPagination(p => ({ ...p, current: pg, total: res.data.total }));
      }
    } catch { notification.error({ message: 'Load failed' }); }
    finally { setLoading(false); }
  }, [typeFilter, pagination.pageSize]);

  useEffect(() => { loadList(1); }, [typeFilter]); // eslint-disable-line

  const remove = async (id) => {
    try {
      await axios.delete(`${baseUrl}/admin/notifications/${id}`, { headers: authH() });
      notification.success({ message: 'Deleted' });
      loadList(pagination.current);
    } catch { notification.error({ message: 'Delete failed' }); }
  };

  const columns = [
    { title: '#', dataIndex: 'id', width: 55 },
    {
      title: 'Title / Message', dataIndex: 'title',
      render: (t, r) => (
        <div>
          <div style={{ fontWeight: 600 }}>{t}</div>
          <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{(r.message || '').slice(0, 80)}…</div>
        </div>
      ),
    },
    {
      title: 'Type', dataIndex: 'type', width: 160,
      render: t => {
        const m = TYPE_META[t] || { label: t, color: 'default' };
        return <Tag color={m.color}>{m.label}</Tag>;
      },
    },
    {
      title: 'Scope', dataIndex: 'recipient_scope', width: 130,
      render: s => <span style={{ fontSize: 12 }}>{SCOPE_META[s] || s}</span>,
    },
    {
      title: 'Sent', dataIndex: 'total_sent', width: 60, align: 'center',
      render: v => <span style={{ fontWeight: 700, color: '#1d4ed8' }}>{v ?? '—'}</span>,
    },
    {
      title: 'Seen', dataIndex: 'read_count', width: 80, align: 'center',
      render: (v, r) => (
        <Tooltip title="Click to see who read this">
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            style={{ color: v > 0 ? '#15803d' : '#9ca3af', fontWeight: 700, padding: 0 }}
            onClick={() => setSeenNotifId(r.id)}
          >
            {v ?? 0}
          </Button>
        </Tooltip>
      ),
    },
    {
      title: 'Channels', dataIndex: 'channels', width: 120,
      render: v => {
        const ch = typeof v === 'string' ? JSON.parse(v) : (v || []);
        return (
          <Space size={3}>
            {ch.includes('portal')   && <Tag color="blue"  style={{ fontSize: 10 }}>Portal</Tag>}
            {ch.includes('whatsapp') && <Tag color="green" style={{ fontSize: 10 }}>WhatsApp</Tag>}
          </Space>
        );
      },
    },
    {
      title: 'Date', dataIndex: 'created_at', width: 110,
      render: d => d
        ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
        : '—',
    },
    {
      title: '', width: 60,
      render: (_, r) => (
        <Popconfirm title="Delete?" onConfirm={() => remove(r.id)}>
          <Button size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  return (
    <div className="layout-content">
      <Card
        title={<span style={{ color: '#fff', fontWeight: 700 }}>Portal Notifications — Sent Log</span>}
        headStyle={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0' }}
        extra={
          <Space>
            <Select
              placeholder="Filter by type"
              value={typeFilter || undefined}
              onChange={v => setTypeFilter(v || '')}
              allowClear
              style={{ width: 180 }}
            >
              {Object.entries(TYPE_META).map(([k, m]) => (
                <Select.Option key={k} value={k}><Tag color={m.color}>{m.label}</Tag></Select.Option>
              ))}
            </Select>
            <Button icon={<ReloadOutlined />} onClick={() => loadList(pagination.current)}>Refresh</Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => history.push('/admin-notifications/send')}
              style={{ background: '#7a0000', borderColor: '#7a0000' }}
            >
              New Notification
            </Button>
          </Space>
        }
      >
        <Table
          dataSource={data}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            onChange: pg => loadList(pg),
            showTotal: t => `Total ${t}`,
          }}
        />
      </Card>

      {seenNotifId && (
        <SeenByModal notifId={seenNotifId} onClose={() => setSeenNotifId(null)} />
      )}
    </div>
  );
}
